import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  alpha,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PlaceIcon from "@mui/icons-material/Place";
import { useNavigate } from "react-router-dom";
import { useGetallStudentQuery } from "../../../Api/attoDeskApi";

/**
 * Student-first entry point for the "New Class" button on Class Sessions.
 *
 * Opens instead of the generic Create Class popup. The admin searches for the
 * student the class should be assigned to, then we route to the full
 * `/assign/classes/create/student/:id` page — which is the workflow that
 * actually shows recommendations, billing setup, etc.
 *
 * The search hits the existing `/student/getAll` endpoint (already scoped by
 * the current user's permissions) and passes the return URL as a
 * querystring hint so the Assign page can bounce back to Class Sessions.
 */

export interface SelectStudentDialogProps {
  open: boolean;
  onClose: () => void;
  /** Where to return after the assign flow — passed to the Assign page as
   *  ?return_url= so it can wire Cancel + post-save navigation. */
  returnUrl?: string;
  /** Preselect a specific class on the Assign page. Used by the
   *  "Continue to Assign Student" workflow that starts from Class
   *  Schedules after a class was just created. */
  selectedClassId?: number | string | null;
  /** Origin tag routed to the Assign page as `?source=…`. Defaults to
   *  the class-sessions caller for back-compat. */
  source?: string;
}

const cap = (s: string | null | undefined) =>
  s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "";

const fullName = (r: any) =>
  [cap(r?.first_name), cap(r?.middle_name), cap(r?.last_name)].filter(Boolean).join(" ") || "—";

const initials = (r: any) =>
  ((cap(r?.first_name)?.[0] || "") + (cap(r?.last_name)?.[0] || "")) || "?";

const STATUS_CHIP: Record<string, { bg: string; color: string }> = {
  active:  { bg: "#dcfce7", color: "#166534" },
  dropped: { bg: "#fee2e2", color: "#991b1b" },
  hold:    { bg: "#fef3c7", color: "#92400e" },
};

const SelectStudentDialog: React.FC<SelectStudentDialogProps> = ({ open, onClose, returnUrl, selectedClassId, source }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [rawSearch, setRawSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Debounce (300ms). Only fire the query when the trimmed input is ≥ 2 chars.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(rawSearch.trim()), 300);
    return () => clearTimeout(t);
  }, [rawSearch]);

  useEffect(() => {
    if (open) {
      setRawSearch("");
      setDebouncedSearch("");
      // MUI focuses the first focusable element by default, but the search
      // field is what we want highlighted — nudge focus after the render.
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const shouldSearch = debouncedSearch.length >= 2;

  const { data, isFetching } = useGetallStudentQuery(
    {
      searchText: debouncedSearch,
      id: "",
      page: 1,
      perPage: 12,
    } as any,
    { skip: !open || !shouldSearch }
  );

  const results: any[] = useMemo(() => {
    const raw = (data as any)?.data;
    return Array.isArray(raw) ? raw.slice(0, 12) : [];
  }, [data]);

  const handleSelect = (student: any) => {
    const params = new URLSearchParams();
    params.set("source", source || "class-sessions");
    if (returnUrl) params.set("return_url", returnUrl);
    if (selectedClassId !== undefined && selectedClassId !== null && selectedClassId !== "") {
      params.set("selected_class_id", String(selectedClassId));
    }
    navigate(`/assign/classes/create/student/${student.id}?${params.toString()}`);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3, minHeight: { xs: "auto", md: 480 } } }}
    >
      <DialogTitle sx={{ pr: 1.5, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 800, letterSpacing: "-0.01em", color: "#0f172a" }}>
            Select Student
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: "#64748b", mt: 0.25 }}>
            Search for a student to assign to an existing class or create a suitable new class.
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ p: 2, pb: 1.25 }}>
          <TextField
            inputRef={inputRef}
            autoFocus
            fullWidth
            size="small"
            value={rawSearch}
            onChange={(e) => setRawSearch(e.target.value)}
            placeholder="Search by student name, ID, phone, email, parent, or guardian…"
            onKeyDown={(e) => {
              if (e.key === "Enter" && results.length > 0) {
                const first = results.find((r) => (r.status || "active").toLowerCase() === "active") || results[0];
                if ((first?.status || "active").toLowerCase() === "active") handleSelect(first);
              }
              if (e.key === "Escape") onClose();
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                </InputAdornment>
              ),
              endAdornment: isFetching ? (
                <InputAdornment position="end">
                  <CircularProgress size={16} />
                </InputAdornment>
              ) : rawSearch ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setRawSearch("")} aria-label="Clear search">
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
              sx: { borderRadius: "10px", fontSize: 13 },
            }}
          />
        </Box>

        <Box sx={{ px: 2, pb: 2, minHeight: 260 }} role="listbox" aria-label="Student search results">
          {!shouldSearch ? (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <PersonOutlineIcon sx={{ fontSize: 40, color: "#cbd5e1", mb: 1 }} />
              <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                Search for a student to continue.
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: "#94a3b8", mt: 0.5 }}>
                Type at least 2 characters — search matches name, ID, phone, email or parent.
              </Typography>
            </Box>
          ) : isFetching && results.length === 0 ? (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <CircularProgress size={24} />
            </Box>
          ) : results.length === 0 ? (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                No students match your search.
              </Typography>
              <Button size="small" onClick={() => setRawSearch("")} sx={{ mt: 1, textTransform: "none", fontWeight: 700 }}>
                Clear Search
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {results.map((s) => {
                const status = String(s?.status || "active").toLowerCase();
                const chip = STATUS_CHIP[status] || STATUS_CHIP.active;
                const isActive = status === "active";
                const gradeName = s?.grade?.grade || s?.grade_name;
                const locationName = s?.location?.location_name || s?.location_name;
                const parentName = s?.parent
                  ? [s.parent.first_name, s.parent.last_name].filter(Boolean).join(" ")
                  : null;
                return (
                  <Box
                    key={s.id}
                    role="option"
                    aria-disabled={!isActive}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      p: 1.25,
                      borderRadius: "12px",
                      border: `1px solid ${alpha("#0f172a", 0.08)}`,
                      bgcolor: "#fff",
                      opacity: isActive ? 1 : 0.65,
                      transition: "border-color .15s, box-shadow .15s",
                      "&:hover": isActive
                        ? {
                            borderColor: alpha(theme.palette.primary.main, 0.4),
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`,
                          }
                        : {},
                    }}
                  >
                    <Avatar
                      src={s?.image || undefined}
                      alt={fullName(s)}
                      sx={{ width: 40, height: 40, bgcolor: alpha(theme.palette.primary.main, 0.12), color: theme.palette.primary.main, fontSize: 13, fontWeight: 700 }}
                    >
                      {initials(s)}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: "#0f172a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {fullName(s)}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>
                          ID #{s.id}
                        </Typography>
                        <Chip
                          label={cap(status)}
                          size="small"
                          sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: chip.bg, color: chip.color, "& .MuiChip-label": { px: 0.75 } }}
                        />
                      </Box>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 0.35, alignItems: "center" }}>
                        {gradeName && (
                          <Typography sx={{ fontSize: 11.5, color: "#334155" }}>
                            <b>Grade:</b> {gradeName}
                          </Typography>
                        )}
                        {parentName && (
                          <Typography sx={{ fontSize: 11.5, color: "#334155" }}>
                            <b>Parent:</b> {parentName}
                          </Typography>
                        )}
                        {locationName && (
                          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.25, fontSize: 11.5, color: "#334155" }}>
                            <PlaceIcon sx={{ fontSize: 12, color: "#94a3b8" }} />
                            {locationName}
                          </Box>
                        )}
                      </Box>
                    </Box>
                    <Button
                      size="small"
                      variant={isActive ? "contained" : "outlined"}
                      disabled={!isActive}
                      onClick={() => handleSelect(s)}
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        borderRadius: "10px",
                        px: 1.75,
                        flexShrink: 0,
                        ...(isActive
                          ? {
                              background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                              boxShadow: "0 4px 12px rgba(37,99,235,0.25)",
                            }
                          : {}),
                      }}
                    >
                      {isActive ? "Select" : "Inactive"}
                    </Button>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SelectStudentDialog;
